import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

/**
 * A native `<select>` with the app's own chevron. The browser draws its arrow
 * against the right border and ignores `padding-right`, so it can't be inset;
 * the native one is hidden and an icon drawn in its place, clear of the edge.
 *
 * `className` sizes the control (e.g. `w-auto`) and lands on the wrapper; every
 * other prop — `ref` included — lands on the `<select>` itself, so it stays a
 * valid tooltip trigger and form element.
 */
function NativeSelect({
  className,
  children,
  ...props
}: React.ComponentProps<"select">) {
  return (
    <span
      data-slot="native-select-wrapper"
      className={cn("relative inline-flex w-full min-w-0", className)}
    >
      <select
        data-slot="native-select"
        className={cn(
          "peer border-input placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 h-8 w-full min-w-0 appearance-none rounded-md border bg-transparent py-1 pr-8 pl-3 text-sm transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          // The native option popup defaults to the browser's light background;
          // give options an explicit themed bg/text so they stay readable in dark mode.
          "[&_option]:bg-popover [&_option]:text-popover-foreground",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          "focus-visible:aria-invalid:ring-ring/50 focus-visible:aria-invalid:border-ring",
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 peer-disabled:opacity-50"
      />
    </span>
  );
}

export { NativeSelect };
